import type { SectionPagination } from '#shared/utils/journal-pagination'
import { SHOW_TEXT } from '#shared/utils/dom'
import type { HtmlParser, HtmlParserPlugin } from '~/plugins/dom-parser'
import { consola } from 'consola'

export interface SectionLayout {
  id: string
  title?: string
  html: string
  pagination?: SectionPagination
}

interface LayoutSource {
  id: string
  title?: string
  bodytext?: string
  pagination?: SectionPagination
}

export interface TextPosition {
  node: Text
  offset: number
}

export interface RunningText {
  text: string
  at: (index: number) => TextPosition
}

// Captions, table cells, list items and footnote bodies are not prose; headings are not <p>.
const NON_PROSE_ANCESTORS = 'figure, table, li, .footnote__content'
// Footnote bodies and the figure fallback the normalizer injects are never print running text, whatever text they carry.
const NOT_RUNNING_TEXT = '.footnote__content, .jats-graphic-fallback'
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u
const PAGE_BREAK_CLASS = 'jats-page-break'

// The HTML parser reads a body-level <title> as RCDATA, so inline markup inside a heading would become literal text.
export function convertJatsTitleTagsToH3(html: string): string {
  return html
    .replace(/<title(\s[^>]*)?>/gi, (_match, attrs) => `<h3${attrs || ''}>`)
    .replace(/<\/title>/gi, '</h3>')
}

function numberedParagraphs(doc: Document): HTMLParagraphElement[] {
  return Array.from(doc.body.querySelectorAll('p')).filter(p => !p.closest(NON_PROSE_ANCESTORS))
}

export function countNumberedParagraphs(doc: Document): number {
  return numberedParagraphs(doc).length
}

export function numberParagraphs(start: number): HtmlParserPlugin {
  return (doc) => {
    numberedParagraphs(doc).forEach((p, index) => {
      const number = String(start + index)
      p.id = `avsnitt-${number}`
      p.setAttribute('data-avsnitt', number)
    })
    return doc
  }
}

export function mergeLeadingLabel(title: string | undefined): { plugin: HtmlParserPlugin, mergedTitle: () => string | undefined } {
  let mergedTitle = title

  const plugin: HtmlParserPlugin = (doc) => {
    if (!title) {
      return doc
    }

    const firstMeaningfulNode = Array.from(doc.body.childNodes).find(node =>
      node.nodeType === 1 || (node.nodeType === 3 && Boolean(node.textContent?.trim())),
    )
    if (!firstMeaningfulNode || firstMeaningfulNode.nodeType !== 1) {
      return doc
    }

    const firstElement = firstMeaningfulNode as Element
    if (firstElement.tagName.toLowerCase() !== 'label') {
      return doc
    }

    const labelText = firstElement.textContent?.replace(/\s+/g, ' ').trim()
    firstElement.remove()
    if (labelText && !(title.startsWith(`${labelText} `) || title === labelText)) {
      mergedTitle = `${labelText} ${title}`
    }
    return doc
  }

  return { plugin, mergedTitle: () => mergedTitle }
}

// Whitespace and punctuation differ between the JATS source and the normalized DOM (footnote spacing, moved commas); letters and digits do not.
export function matchKey(text: string): string {
  return Array.from(text.normalize('NFC')).filter(char => LETTER_OR_DIGIT.test(char)).join('')
}

export function sectionRunningText(doc: Document): RunningText {
  const chars: string[] = []
  const positions: TextPosition[] = []
  const walker = doc.createTreeWalker(doc.body, SHOW_TEXT)
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    if (node.parentElement?.closest(NOT_RUNNING_TEXT)) {
      continue
    }
    const normalized = node.data.normalize('NFC')
    if (normalized !== node.data) {
      node.data = normalized
    }
    let offset = 0
    for (const char of normalized) {
      if (LETTER_OR_DIGIT.test(char)) {
        chars.push(char)
        for (let unit = 0; unit < char.length; unit += 1) { positions.push({ node, offset }) }
      }
      offset += char.length
    }
  }
  return { text: chars.join(''), at: index => positions[index]! }
}

function uniqueIndex(running: string, key: string): number | undefined {
  const first = key.length > 0 ? running.indexOf(key) : -1
  return first !== -1 && running.indexOf(key, first + 1) === -1 ? first : undefined
}

export function validateSectionPagination(doc: Document, pagination: SectionPagination | undefined, sectionId: string): SectionPagination | undefined {
  if (!pagination) {
    return undefined
  }
  const { text } = sectionRunningText(doc)
  let previous = -1
  for (const item of pagination.breaks) {
    const index = uniqueIndex(text, matchKey(item.text))
    // Reverse-order insertion assumes the matches ascend with the pages; a snippet out of order would misplace a marker.
    if (index === undefined || index <= previous) {
      consola.warn('[journal-pagination] dropped', { sectionId, page: item.page })
      return undefined
    }
    previous = index
  }
  return pagination
}

function tokenStart(position: TextPosition): number {
  let { offset } = position
  while (offset > 0 && !/\s/.test(position.node.data[offset - 1]!)) {
    offset -= 1
  }
  return offset
}

function createMarker(doc: Document, page: number): HTMLSpanElement {
  const span = doc.createElement('span')
  span.className = PAGE_BREAK_CLASS
  span.id = `side-${page}`
  span.setAttribute('data-page', String(page))
  span.setAttribute('aria-hidden', 'true')
  return span
}

export function insertPageBreaks(pagination: SectionPagination): HtmlParserPlugin {
  return (doc) => {
    const running = sectionRunningText(doc)
    // Splitting a text node invalidates later offsets in the same node, so insert from the last break backwards.
    for (const item of [...pagination.breaks].reverse()) {
      const position = running.at(running.text.indexOf(matchKey(item.text)))
      const offset = tokenStart(position)
      const marker = createMarker(doc, item.page)
      const anchor = offset === 0 ? position.node : position.node.splitText(offset)
      anchor.parentNode?.insertBefore(marker, anchor)
    }
    return doc
  }
}

export function buildSectionLayouts(sections: LayoutSource[], parse: HtmlParser<Document>): SectionLayout[] {
  let counter = 1

  return sections.map((section) => {
    const label = mergeLeadingLabel(section.title)
    try {
      const doc = parse(convertJatsTitleTagsToH3(section.bodytext ?? ''), [label.plugin, 'normalize-jats', numberParagraphs(counter)])
      const pagination = validateSectionPagination(doc, section.pagination, section.id)
      if (pagination) {
        insertPageBreaks(pagination)(doc)
      }
      counter += countNumberedParagraphs(doc)
      return { id: section.id, title: label.mergedTitle(), html: doc.body.innerHTML.trim(), pagination }
    }
    catch {
      consola.warn('[section-layout] parse failed, rendering the raw section', { sectionId: section.id })
      return { id: section.id, title: section.title, html: section.bodytext ?? '' }
    }
  })
}
